import { createContext, useContext, useState } from "react";

export const STR = {
  en: {
    nav: {
      rooms: "Rooms", explore: "Explore", food: "Food Menu", book: "Book Now",
      platforms: "Platforms", contact: "Contact", bookNow: "Book Now",
    },
    hero: {
      line1: "A Divine Stay", line2: "in the Heart of", line3: "Vrindavan Dham",
      sub: "A peaceful guest house in Chaitanya Vihar — minutes from Banke Bihari Ji, Prem Mandir and the ghats of Maa Yamuna. Pure-veg kitchen, warm seva, and rooms made for rest.",
      cta1: "Book Your Stay", cta2: "Explore Rooms",
      highlights: ["Chaitanya Vihar, Vrindavan", "100% Pure-Veg Kitchen", "24×7 Room Service"],
    },
    rooms: {
      overline: "Stay With Us · निवास",
      heading: "Rooms for every yatra.",
      intro: "Three thoughtfully kept room types — for couples, yatra groups and full families. Tariffs shared instantly on WhatsApp.",
      price: "Price on enquiry",
      enquire: "Enquire on WhatsApp",
    },
    roomData: {
      "nidhivan-deluxe": {
        type: "Deluxe Room", capacity: "2 Adults", beds: "1 King Size Bed / 2 Single Beds",
        blurb: "A calm, cool retreat for two — crisp linen, warm wood and everything you need after evening darshan.",
        features: ["Air Conditioning", "Smart TV", "Geyser", "Western Toilet", "Room Service"],
      },
      "seva-kunj-triple": {
        type: "3 Bed Deluxe Room", capacity: "3 Adults", beds: "3 Single Beds / 1 King + 1 Single",
        blurb: "Made for yatra groups of three — flexible bedding, hot water on tap and room service till late.",
        features: ["Smart TV", "Western Toilet", "Geyser", "Room Service"],
      },
      "banke-bihari-family": {
        type: "4 Bed Deluxe Room", capacity: "4+ Guests · Best for Families", beds: "2 King Size Beds",
        blurb: "Two king beds under one roof — the whole parivar stays together, minutes from Banke Bihari Ji.",
        features: ["Air Conditioning", "Smart TV", "Attached Western Toilet", "Geyser", "Room Service"],
      },
    },
    booking: {
      overline: "Reserve · बुकिंग",
      heading: "Book your stay in 30 seconds.",
      sub: "Fill this in and it opens WhatsApp with everything pre-written — hit send and our team confirms availability & tariff right away.",
      labels: { name: "Your Name", phone: "Phone / WhatsApp", checkin: "Check-in", checkout: "Check-out", room: "Room Type", guests: "Guests", note: "Special Requests (optional)" },
      ph: { name: "Full name", phone: "10-digit mobile", note: "Early check-in, extra bedding, darshan timing…" },
      errors: { required: "Please fill your name, phone and both dates.", dates: "Check-out date must be after check-in date." },
      submit: "Send Booking on WhatsApp",
      direct: {
        title: "Why book direct?",
        bullets: [
          "Best tariff — no platform commission added to your rate.",
          "Instant confirmation on WhatsApp, usually within minutes.",
          "Pure-veg in-room dining from our own Vrindavan kitchen.",
          "Flexible check-in help for mangala aarti travellers.",
          "Talk directly to the family that runs the dham — +91 63993 33394.",
        ],
        quoteSub: "The guest is God — the only policy we truly need.",
      },
    },
    food: {
      overline: "In-Room Dining · भोजन",
      heading: "Prasad-like food, to your room.",
      note: "100% pure vegetarian. Jain & no onion-garlic preparations on request. Order here — it lands on our WhatsApp instantly.",
      cats: ["Breakfast", "Thali & Meals", "Main Course", "Breads & Rice", "Snacks", "Sweets & Drinks"],
      add: "Add",
      reviewOrder: "Review & Order",
      items: "items", item: "item",
      yourOrder: "Your Order", total: "Total",
      namePh: "Your name", roomPh: "Room no. (if staying)",
      orderBtn: "Send Order on WhatsApp",
      modalNote: "Order goes straight to our kitchen WhatsApp — we confirm in minutes.",
      toastSuffix: "added to your order",
    },
    explore: {
      overline: "Explore Braj · ब्रज दर्शन",
      heading: "Vrindavan, planned for you.",
      note: "Six unmissable darshans, all a short ride from the dham — and a ready-made plan below. Temple timings shift with seasons and festivals; we confirm same-day timings for you at reception.",
      places: [
        { name: "Banke Bihari Ji Mandir", desc: "The beating heart of Vrindavan. Darshan happens in quick jhankis as the curtain opens and closes — a tradition found nowhere else in the world.", tip: "Go early morning; darshan roughly 8–11 AM & 5–8 PM" },
        { name: "Prem Mandir", desc: "A white-marble wonder carved with Krishna's leelas. Stay after sunset when the whole temple glows and the musical fountain begins.", tip: "Most magical after sunset — illumination from ~7 PM" },
        { name: "Nidhivan", desc: "The sacred tulsi grove where Radha-Krishna's raas leela is believed to happen every night. Gates close at dusk — even the monkeys leave.", tip: "Visit before dusk; pair with nearby Seva Kunj" },
        { name: "ISKCON Krishna Balaram Mandir", desc: "Marble courtyard, 24-hour kirtan and hot prasadam. The 4:30 AM mangala aarti here is an experience devotees travel continents for.", tip: "Mangala aarti 4:30 AM; evening aarti ~7 PM" },
        { name: "Kesi Ghat & Yamuna Aarti", desc: "Lamps, chants and boats on Maa Yamuna — the evening aarti here is Vrindavan's most peaceful hour, on the ghat where Krishna slew Kesi.", tip: "Reach by 6 PM; take a sunset boat ride" },
        { name: "Radha Raman & Old Town Lanes", desc: "A self-manifested deity from 1542, plus Shahji, Radha Vallabh and Govind Dev Ji hidden in the old lanes — Vrindavan as it has always been.", tip: "Best explored on foot with a local guide" },
      ],
      itin: {
        overline: "The Perfect Vrindavan Plan · यात्रा योजना",
        heading: "Two days, every darshan.",
        cta: "Ask us to plan your darshan",
        days: [
          { day: "Day 1", title: "Old Vrindavan", steps: [
            ["4:30 AM", "Mangala aarti at ISKCON (optional, worth it)"],
            ["7:30 AM", "Banke Bihari Ji darshan — beat the rush"],
            ["9:00 AM", "Bedmi kachori breakfast in the old lanes"],
            ["10:00 AM", "Nidhivan, Seva Kunj & Radha Raman walk"],
            ["1:00 PM", "Lunch & rest at Shri Jamuna Dham"],
            ["5:30 PM", "Kesi Ghat — Yamuna aarti & boat ride"],
            ["8:00 PM", "Pure-veg dinner served in your room"],
          ]},
          { day: "Day 2", title: "New Vrindavan & Beyond", steps: [
            ["9:00 AM", "Prem Mandir — morning darshan & gardens"],
            ["11:00 AM", "ISKCON daytime darshan & prasadam"],
            ["1:00 PM", "Lunch & rest at the dham"],
            ["6:30 PM", "Prem Mandir illumination & fountain show"],
            ["Day 3+", "Mathura Janmabhoomi, Govardhan parikrama or Barsana — we arrange autos & taxis"],
          ]},
        ],
      },
    },
    platforms: {
      overline: "Also Listed On · हमारे साथी",
      heading: "Book on your favourite platform.",
      direct: "Best rate — book direct",
      tags: ["Genius deals", "India's favourite", "goCash rewards", "Global rates", "Homestays"],
    },
    directions: {
      overline: "Getting Here · मार्ग",
      heading: "Turn-by-turn, straight to our door.",
      text: "100 Futa Road, near the Flyover, Chaitanya Vihar. Tap below and your phone's maps app will guide you right to Shri Jamuna Dham.",
      cta: "Get Directions",
    },
    reviews: {
      overline: "Guest Love · अतिथि वाणी",
      heading: "Rated by our guests on Google.",
      reviewsWord: "reviews", reviewWord: "review",
      fallbackTitle: "5.0 rated guest house on Google",
      fallbackText: "Every guest review so far has been 5 stars — read them on our Google Business Profile.",
      readAll: "Read all reviews on Google",
      write: "Write a review",
    },
    location: {
      overline: "Find Us · पता",
      heading: "In the heart of Chaitanya Vihar.",
      whatsapp: "WhatsApp Us",
      google: "Find us on Google",
    },
    footer: {
      tagline: "A family-run guest house in the divine land of Shri Radha-Krishna. Come as a guest, leave as family.",
      explore: "Explore", contact: "Contact",
      rights: "All rights reserved.",
      links: [["Rooms", "#rooms"], ["Explore Vrindavan", "#explore"], ["Food Menu", "#food"], ["Book Now", "#booking"], ["Platforms", "#platforms"], ["Contact", "#contact"]],
    },
    about: {
      overline: "Our Dham · हमारा धाम",
      heading: "Seva, not just service.",
      chapterWord: "CHAPTER",
      chapters: [
        { title: "Darshan Ready", text: "Wake up in Chaitanya Vihar and walk into the bhav of Braj. Banke Bihari Ji, Prem Mandir, ISKCON and Nidhivan are all a short ride away — we help you plan mangala aarti mornings and evening darshan without the rush." },
        { title: "Seva Bhav", text: "Hot water on tap, crisp linen, Smart TV and room service that treats you like family, not a booking ID. Every corner is cleaned and prepared the way we would for a guest in our own home — atithi devo bhava." },
        { title: "Sattvic Bhojan", text: "Our kitchen is 100% pure vegetarian, cooked in the sattvic tradition of Vrindavan — bedmi kachori, creamy lassi in kulhads, and thalis that taste like prasad. Jain and no onion-garlic preparations on request, served hot in your room." },
      ],
    },
    banner: {
      overline: "Festival Alert · त्योहार",
      heading: {
        janmashtami: "Vrindavan sells out weeks before Janmashtami.",
        holi: "Braj Holi — the world's most colourful festival.",
      },
      text: {
        janmashtami: "4 September 2026 — the biggest celebration at Banke Bihari Ji. Secure your room now; finding a bed later is nearly impossible.",
        holi: "March 2027 — rooms for Barsana, Nandgaon and Vrindavan Holi get booked months in advance.",
      },
      cta: "Book Early on WhatsApp",
      daysLeft: "days to go",
    },
    float: { tooltip: "Chat with our team" },
  },

  hi: {
    nav: {
      rooms: "कमरे", explore: "दर्शन", food: "भोजन मेनू", book: "बुक करें",
      platforms: "प्लेटफ़ॉर्म", contact: "संपर्क", bookNow: "बुक करें",
    },
    hero: {
      line1: "एक दिव्य ठहराव", line2: "के हृदय में", line3: "वृन्दावन धाम",
      sub: "चैतन्य विहार में एक शांत अतिथि गृह — बाँके बिहारी जी, प्रेम मंदिर और माँ जमुना के घाटों से कुछ ही मिनट की दूरी पर। शुद्ध शाकाहारी रसोई, स्नेहिल सेवा और आराम के लिए बने कमरे।",
      cta1: "अपना प्रवास बुक करें", cta2: "कमरे देखें",
      highlights: ["चैतन्य विहार, वृन्दावन", "100% शुद्ध शाकाहारी रसोई", "24×7 रूम सर्विस"],
    },
    rooms: {
      overline: "Stay With Us · निवास",
      heading: "हर यात्रा के लिए कमरे।",
      intro: "तीन सुसज्जित कमरे — जोड़ों, यात्रा दलों और पूरे परिवार के लिए। किराया WhatsApp पर तुरंत जानें।",
      price: "किराया पूछें",
      enquire: "WhatsApp पर पूछताछ करें",
    },
    roomData: {
      "nidhivan-deluxe": {
        type: "डीलक्स रूम", capacity: "2 वयस्क", beds: "1 किंग साइज़ बेड / 2 सिंगल बेड",
        blurb: "दो लोगों के लिए शांत, ठंडा ठहराव — मुलायम चादरें और संध्या दर्शन के बाद की हर सुविधा।",
        features: ["एयर कंडीशनर", "स्मार्ट टीवी", "गीज़र", "वेस्टर्न टॉयलेट", "रूम सर्विस"],
      },
      "seva-kunj-triple": {
        type: "3 बेड डीलक्स रूम", capacity: "3 वयस्क", beds: "3 सिंगल बेड / 1 किंग + 1 सिंगल",
        blurb: "तीन के यात्रा दल के लिए — लचीली बेडिंग, गर्म पानी और देर रात तक रूम सर्विस।",
        features: ["स्मार्ट टीवी", "वेस्टर्न टॉयलेट", "गीज़र", "रूम सर्विस"],
      },
      "banke-bihari-family": {
        type: "4 बेड डीलक्स रूम", capacity: "4+ मेहमान · परिवार हेतु उत्तम", beds: "2 किंग साइज़ बेड",
        blurb: "एक ही छत के नीचे दो किंग बेड — पूरा परिवार साथ रहे, बाँके बिहारी जी से कुछ ही मिनट।",
        features: ["एयर कंडीशनर", "स्मार्ट टीवी", "अटैच्ड वेस्टर्न टॉयलेट", "गीज़र", "रूम सर्विस"],
      },
    },
    booking: {
      overline: "Reserve · बुकिंग",
      heading: "30 सेकंड में अपना प्रवास बुक करें।",
      sub: "यह फ़ॉर्म भरें — WhatsApp पर सब कुछ पहले से लिखा खुलेगा; भेजते ही हमारी टीम उपलब्धता और किराया तुरंत बता देगी।",
      labels: { name: "आपका नाम", phone: "फ़ोन / WhatsApp", checkin: "चेक-इन", checkout: "चेक-आउट", room: "कमरे का प्रकार", guests: "मेहमान", note: "विशेष अनुरोध (वैकल्पिक)" },
      ph: { name: "पूरा नाम", phone: "10 अंकों का मोबाइल", note: "जल्दी चेक-इन, अतिरिक्त बेडिंग, दर्शन समय…" },
      errors: { required: "कृपया नाम, फ़ोन और दोनों तिथियाँ भरें।", dates: "चेक-आउट तिथि चेक-इन के बाद होनी चाहिए।" },
      submit: "WhatsApp पर बुकिंग भेजें",
      direct: {
        title: "सीधे बुक करने के लाभ?",
        bullets: [
          "सबसे अच्छा किराया — प्लेटफ़ॉर्म कमीशन नहीं जुड़ता।",
          "WhatsApp पर तुरंत पुष्टि, आमतौर पर कुछ ही मिनटों में।",
          "हमारी अपनी वृन्दावन रसोई से शुद्ध शाकाहारी भोजन कमरे में।",
          "मंगला आरती यात्रियों के लिए लचीली चेक-इन सहायता।",
          "सीधे धाम चलाने वाले परिवार से बात करें — +91 63993 33394।",
        ],
        quoteSub: "अतिथि देवो भवः — हमारी एकमात्र सच्ची नीति।",
      },
    },
    food: {
      overline: "In-Room Dining · भोजन",
      heading: "प्रसाद-सा भोजन, सीधे आपके कमरे में।",
      note: "100% शुद्ध शाकाहारी। जैन व बिना प्याज़-लहसुन भोजन अनुरोध पर। यहाँ ऑर्डर करें — सीधे हमारे WhatsApp पर पहुँचता है।",
      cats: ["नाश्ता", "थाली व भोजन", "मुख्य व्यंजन", "रोटी व चावल", "स्नैक्स", "मिठाई व पेय"],
      add: "जोड़ें",
      reviewOrder: "देखें व ऑर्डर करें",
      items: "वस्तुएँ", item: "वस्तु",
      yourOrder: "आपका ऑर्डर", total: "कुल",
      namePh: "आपका नाम", roomPh: "कमरा नं. (यदि ठहरे हैं)",
      orderBtn: "WhatsApp पर ऑर्डर भेजें",
      modalNote: "ऑर्डर सीधे हमारी रसोई के WhatsApp पर — हम कुछ ही मिनटों में पुष्टि करते हैं।",
      toastSuffix: "ऑर्डर में जुड़ गया",
    },
    explore: {
      overline: "Explore Braj · ब्रज दर्शन",
      heading: "वृन्दावन, आपके लिए सुनियोजित।",
      note: "छह अनिवार्य दर्शन, सभी धाम से कुछ ही दूरी पर — और नीचे तैयार योजना। मंदिरों का समय ऋतु व त्योहारों से बदलता है; हम रिसेप्शन पर उसी दिन का समय बता देते हैं।",
      places: [
        { name: "बाँके बिहारी जी मंदिर", desc: "वृन्दावन की धड़कन। पर्दा खुलते-बंद होते ही झाँकी दर्शन होता है — ऐसी परंपरा दुनिया में कहीं नहीं।", tip: "सुबह जल्दी जाएँ; दर्शन लगभग 8–11 AM व 5–8 PM" },
        { name: "प्रेम मंदिर", desc: "श्वेत संगमरमर का अद्भुत मंदिर, कृष्ण लीलाओं की नक्काशी से सज्जित। सूर्यास्त के बाद पूरा मंदिर जगमगाता है और संगीतमय झरना शुरू होता है।", tip: "सूर्यास्त के बाद सबसे सुंदर — ~7 PM से प्रकाश" },
        { name: "निधिवन", desc: "पवित्र तुलसी वन जहाँ हर रात राधा-कृष्ण की रासलीला होती है मानी जाती है। सूर्यास्त पर द्वार बंद — बंदर भी चले जाते हैं।", tip: "सूर्यास्त से पहले जाएँ; पास का सेवा कुंज भी देखें" },
        { name: "इस्कॉन कृष्ण बलराम मंदिर", desc: "संगमरमर का आँगन, 24 घंटे कीर्तन और गरम प्रसादम्। यहाँ की 4:30 AM मंगला आरती अविस्मरणीय है।", tip: "मंगला आरती 4:30 AM; संध्या आरती ~7 PM" },
        { name: "केशी घाट व जमुना आरती", desc: "माँ जमुना पर दीप, भजन और नावें — यहाँ की संध्या आरती वृन्दावन का सबसे शांत क्षण है।", tip: "6 PM तक पहुँचें; सूर्यास्त पर नाव की सैर करें" },
        { name: "राधा रमण व पुरानी गलियाँ", desc: "1542 का स्वयंभू विग्रह, साथ ही पुरानी गलियों में शाहजी, राधा वल्लभ और गोविन्द देव जी — जैसा वृन्दावन सदा से है।", tip: "स्थानीय गाइड के साथ पैदल घूमना सर्वोत्तम" },
      ],
      itin: {
        overline: "The Perfect Vrindavan Plan · यात्रा योजना",
        heading: "दो दिन, सभी दर्शन।",
        cta: "दर्शन योजना के लिए हमें लिखें",
        days: [
          { day: "दिन 1", title: "पुराना वृन्दावन", steps: [
            ["4:30 AM", "इस्कॉन में मंगला आरती (वैकल्पिक, पर अवश्य करें)"],
            ["7:30 AM", "बाँके बिहारी जी दर्शन — भीड़ से पहले"],
            ["9:00 AM", "पुरानी गलियों में बेड़मी कचौड़ी नाश्ता"],
            ["10:00 AM", "निधिवन, सेवा कुंज व राधा रमण पैदल यात्रा"],
            ["1:00 PM", "श्री जमुना धाम में भोजन व विश्राम"],
            ["5:30 PM", "केशी घाट — जमुना आरती व नाव सैर"],
            ["8:00 PM", "कमरे में शुद्ध शाकाहारी भोजन"],
          ]},
          { day: "दिन 2", title: "नया वृन्दावन व आसपास", steps: [
            ["9:00 AM", "प्रेम मंदिर — प्रातः दर्शन व उद्यान"],
            ["11:00 AM", "इस्कॉन दर्शन व प्रसादम्"],
            ["1:00 PM", "धाम में भोजन व विश्राम"],
            ["6:30 PM", "प्रेम मंदिर प्रकाश व झरना शो"],
            ["दिन 3+", "मथुरा जन्मभूमि, गोवर्धन परिक्रमा या बरसाना — हम ऑटो/टैक्सी व्यवस्था करते हैं"],
          ]},
        ],
      },
    },
    platforms: {
      overline: "Also Listed On · हमारे साथी",
      heading: "अपने पसंदीदा प्लेटफ़ॉर्म पर बुक करें।",
      direct: "सबसे अच्छा किराया — सीधे बुक करें",
      tags: ["जीनियस ऑफ़र", "भारत का पसंदीदा", "goCash रिवॉर्ड", "वैश्विक दरें", "होमस्टे"],
    },
    directions: {
      overline: "Getting Here · मार्ग",
      heading: "सीधे हमारे द्वार तक, मोड़-दर-मोड़।",
      text: "100 फुटा रोड, फ्लायओवर के पास, चैतन्य विहार। नीचे टैप करें — आपके फ़ोन का मैप ऐप आपको श्री जमुना धाम तक ले आएगा।",
      cta: "रास्ता देखें",
    },
    reviews: {
      overline: "Guest Love · अतिथि वाणी",
      heading: "Google पर हमारे मेहमानों की रेटिंग।",
      reviewsWord: "समीक्षाएँ", reviewWord: "समीक्षा",
      fallbackTitle: "Google पर 5.0 रेटेड अतिथि गृह",
      fallbackText: "अब तक की हर समीक्षा 5 स्टार — हमारी Google Business Profile पर पढ़ें।",
      readAll: "Google पर सभी समीक्षाएँ पढ़ें",
      write: "समीक्षा लिखें",
    },
    location: {
      overline: "Find Us · पता",
      heading: "चैतन्य विहार के हृदय में।",
      whatsapp: "WhatsApp करें",
      google: "Google पर हमें खोजें",
    },
    footer: {
      tagline: "श्री राधा-कृष्ण की पावन भूमि में एक पारिवारिक अतिथि गृह। मेहमान बनकर आएँ, परिवार बनकर लौटें।",
      explore: "देखें", contact: "संपर्क",
      rights: "सर्वाधिकार सुरक्षित।",
      links: [["कमरे", "#rooms"], ["वृन्दावन दर्शन", "#explore"], ["भोजन मेनू", "#food"], ["बुक करें", "#booking"], ["प्लेटफ़ॉर्म", "#platforms"], ["संपर्क", "#contact"]],
    },
    about: {
      overline: "Our Dham · हमारा धाम",
      heading: "सेवा, केवल सेवा नहीं।",
      chapterWord: "अध्याय",
      chapters: [
        { title: "दर्शन के लिए तैयार", text: "चैतन्य विहार में जागें और ब्रज के भाव में डूब जाएँ। बाँके बिहारी जी, प्रेम मंदिर, इस्कॉन और निधिवन सब कुछ ही दूरी पर — हम मंगला आरती की सुबह और संध्या दर्शन की योजना बिना भागदौड़ के बनवाते हैं।" },
        { title: "सेवा भाव", text: "गर्म पानी, मुलायम चादरें, स्मार्ट टीवी और रूम सर्विस जो आपको बुकिंग आईडी नहीं, परिवार समझती है। हर कोना उसी तरह तैयार होता है जैसे अपने घर के मेहमान के लिए — अतिथि देवो भव।" },
        { title: "सात्विक भोजन", text: "हमारी रसोई 100% शुद्ध शाकाहारी है, वृन्दावन की सात्विक परंपरा में पकती है — बेड़मी कचौड़ी, कुल्हड़ की मलाईदार लस्सी और प्रसाद-सी थाली। जैन व बिना प्याज़-लहसुन भोजन अनुरोध पर, गरम-गरम आपके कमरे में।" },
      ],
    },
    banner: {
      overline: "Festival Alert · त्योहार",
      heading: {
        janmashtami: "जन्माष्टमी पर वृन्दावन हफ़्तों पहले फुल हो जाता है।",
        holi: "ब्रज की होली — दुनिया का सबसे रंगीन उत्सव।",
      },
      text: {
        janmashtami: "4 सितंबर 2026 — बाँके बिहारी जी का सबसे बड़ा उत्सव। कमरा अभी सुरक्षित करें; बाद में जगह मिलना लगभग असंभव है।",
        holi: "मार्च 2027 — बरसाना, नंदगाँव और वृन्दावन की होली के कमरे महीनों पहले बुक होते हैं।",
      },
      cta: "WhatsApp पर जल्दी बुक करें",
      daysLeft: "दिन शेष",
    },
    float: { tooltip: "हमारी टीम से चैट करें" },
  },
};

const LangContext = createContext(null);

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState("en");
  return (
    <LangContext.Provider value={{ lang, setLang, s: STR[lang] }}>
      {children}
    </LangContext.Provider>
  );
};

export const useLang = () => useContext(LangContext);
